import * as React from "react";

import { Banner, BannerContent, Text } from "@/design-system/salt";
export interface AlertProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Banner>,
  "status" | "variant"
> {
  variant?: "default" | "destructive";
  style?: React.CSSProperties;
}

type AlertTextColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "info"
  | "warning"
  | "error"
  | "success";
type AlertTitleProps = Omit<React.ComponentPropsWithoutRef<"h5">, "color"> & {
  color?: AlertTextColor;
};
type AlertDescriptionProps = Omit<React.ComponentPropsWithoutRef<"div">, "color"> & {
  color?: AlertTextColor;
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  // `role` must follow the status, not default to "alert" unconditionally. An
  // unconditional "alert" makes every informational banner (approval pending,
  // enrollment state, goal guidance) interrupt a screen reader assertively as if
  // it were an error. Salt's Banner sets no role of its own, so this default is
  // the only thing deciding it. Mirrors `alert-callout.tsx:36`, which already
  // branches error/warning -> "alert", info/success -> "status". An explicit
  // `role` from the caller still wins.
  ({ children, className, role, style, variant = "default", ...props }, ref) => (
    <Banner
      {...props}
      ref={ref}
      role={role ?? (variant === "destructive" ? "alert" : "status")}
      status={variant === "destructive" ? "error" : "info"}
      variant={variant === "destructive" ? "secondary" : "primary"}
      className={className}
      style={{ alignItems: "flex-start", ...style }}
    >
      <BannerContent style={{ minWidth: 0 }}>{children}</BannerContent>
    </Banner>
  ),
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, color, style, ...props }, ref) => (
    <Text
      {...props}
      as="h5"
      ref={ref}
      styleAs="h4"
      color={color}
      className={className}
      style={{ marginBottom: "var(--salt-spacing-50)", ...style }}
    />
  ),
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<HTMLDivElement, AlertDescriptionProps>(
  // `[&_p]:leading-relaxed` was a Tailwind arbitrary variant and has been dead since
  // Tailwind was uninstalled — it set no line-height on anything. Dropped rather than
  // re-implemented: DESIGN.md gives Salt authority over typography, and Salt's Text
  // already carries the line-height for this scale. Re-adding it by hand would be
  // hand-rolling something Salt owns.
  ({ className, color, ...props }, ref) => (
    <Text {...props} ref={ref} color={color} className={className} />
  ),
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, AlertTitle };
